import { useRef, useState } from "react";
import { Table, Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { format, validate } from "rut.js";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import { toast } from "@/services/Utils";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useBusinessBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";

import LoadInventoryToolbar from "./LoadInventoryToolbar";
import FooterButtons from "../Buttons/FooterButtons";

import "./rowsLoadStyle.scss";

const key = "initial_data_implementation";

const BusinessTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const { mutate, isLoading } = useBusinessBulkLoader();

	const { data } = useInitialDataImplementation();
	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const fileInputRef = useRef(null);

	const addRow = () => {
		const newBusiness = [...data.business, { hash: uuidv4(), name: "", code: "", giro: "", isNew: true }];
		queryClient.setQueryData([key], { ...data, business: newBusiness });
	};

	const handleInputChange = (index, field, value) => {
		let newBusiness;

		if (field === "code") {
			let newValue = format(value);
			newBusiness = data.business.map((row, i) => (i === index ? { ...row, [field]: newValue } : row));
		} else {
			newBusiness = data.business.map((row, i) => (i === index ? { ...row, [field]: value } : row));
		}

		queryClient.setQueryData([key], { ...data, business: newBusiness });
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Accion de borrado",
				html: "Esta accion tendra efecto al presionar <strong>GUARDAR</strong> o <strong>SIGUIENTE</strong>, ¿deseas proceder?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminarlo!",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newBusiness = data.business.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, business: newBusiness });

					Swal.fire({
						title: "Eliminado",
						html: "La razon social ha sido eliminada",
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Accion cancelada por el usuario");
				}
			});
		} else {
			const newBusiness = data.business.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, business: newBusiness });
			toast(addToast, "Razon social eliminada");
		}
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlanillaCargaRazonesSociales.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_business",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const updatedBusiness = [...data.business];
			let duplicatesFound = false;

			const newRows = jsonData
				.map((item) => {
					const hash = item["CODIGO"] || uuidv4();
					const name = item["NOMBRE"] || "";
					const code = item["RUT"] || "";
					const giro = item["GIRO"] || "";

					const isDuplicated = updatedBusiness.some((business) => business.code === code);

					if (isDuplicated) {
						duplicatesFound = true;
						return null;
					} else {
						const isNew = !updatedBusiness.some((business) => business.hash === hash);
						return { hash, name, code, giro, isNew };
					}
				})
				.filter(Boolean);

			updatedBusiness.push(...newRows);

			queryClient.setQueryData([key], { ...data, business: updatedBusiness });

			if (duplicatesFound) {
				toast(addToast, "Algunas razones sociales no fueron cargadas por rut duplicado. Revisa tu archivo y cargalo nuevamente", "info");
			}
		};

		reader.readAsBinaryString(file);

		fileInputRef.current.value = "";
	};

	const validBusiness = () => {
		let isValidBusiness = true;

		for (let i = 0; i < data?.business.length; i++) {
			const business = data.business[i];

			if (!business.name || !business.code) {
				isValidBusiness = false;
				toast(addToast, "Se requiere que las razones sociales tengan al menos nombre y rut para continuar", "error");
				break;
			}

			if (business.code && !validate(business.code)) {
				isValidBusiness = false;
				toast(addToast, "El rut ingresado no es válido. Por favor, revisa el elemento " + (i + 1) + " y corrige el rut.", "error");
				break;
			}
		}

		return isValidBusiness;
	};

	const saveBusiness = ({ moveForward = false }) => {
		if (validBusiness()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "business")) {
						toast(addToast, response.message, "success");

						queryClient.setQueryData([key], {
							...data,
							business: response.business,
							implementationStep: response.implementationStep,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	if (data.business.length === 0) {
		addRow();
	}

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>
			<Table hover>
				<thead>
					<tr>
						<th className="border-0" style={{ width: "32%" }}>
							Nombre <small className="text-danger">*</small>
						</th>
						<th className="border-0" style={{ width: "32%" }}>
							Rut <small className="text-danger">*</small>
						</th>
						<th className="border-0" style={{ width: "32%" }}>
							Giro
						</th>
						<th className="border-0" style={{ width: "4%" }}></th>
					</tr>
				</thead>
				<tbody>
					{data.business.map((row, index) => (
						<tr key={row.hash}>
							<td className="td-collapse position-relative ">
								<input
									type="text"
									className="form-control"
									placeholder="Nombre de la razon social"
									required
									value={row.name || ""}
									onChange={(e) => handleInputChange(index, "name", e.target.value)}
								/>
								{row.isNew && (
									<div className="diagonal-badge">
										<span className="badge badge-info">Nuevo</span>
									</div>
								)}
							</td>
							<td className="td-collapse">
								<input
									type="text"
									className="form-control"
									placeholder="Ingrese un rut"
									required
									value={row.code ? format(row.code) : ""}
									onChange={(e) => handleInputChange(index, "code", e.target.value)}
								/>
							</td>
							<td className="td-collapse">
								<input
									type="text"
									className="form-control"
									placeholder="Ingrese el giro comercial"
									value={row.giro || ""}
									onChange={(e) => handleInputChange(index, "giro", e.target.value)}
								/>
							</td>
							<td className="td-collapse">
								<Button variant="danger" onClick={() => removeRow(index, row.isNew)}>
									<i className="ti-trash"></i>
								</Button>
							</td>
						</tr>
					))}
					<tr>
						<td className="border-0">
							<button
								className="btn bg-agro-outline-color btn-block tr-btn"
								onClick={addRow}
								style={{ height: "30px", fontSize: "12px", width: "200px" }}
								disabled={isLoading}
							>
								<i className="ti-plus"></i> Agregar
							</button>
						</td>
					</tr>
				</tbody>
			</Table>

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveBusiness} isLoading={isLoading} />
		</>
	);
};

export default BusinessTableLoad;
